import {useEffect, useState} from 'react';
import {UpsertProject} from '../components/UpsertProject';
import {useProject} from "../context/ProjectContext";
import {FormFA, ProjectFA} from "../interfaces/global-interfaces";
import {getFormsByProjectId} from "../services/apiService";
import {useParams, useNavigate} from "react-router-dom";
import {ArrowLeft, ArrowUpRight, ChevronDown, FolderKanban, Plus, Pencil, FileText, Search} from "lucide-react";
import {Card} from "../components/ui/Card";
import {Button} from "../components/ui/Button";
import {SearchInput} from "../components/ui/SearchInput";
import {EmptyState} from "../components/ui/EmptyState";
import {Loader} from "../components/ui/Loader";
import {staggerDelay} from "../utils/animation";

export const Projects = () => {
    const {projectId} = useParams<{ projectId: string }>();
    const [expandedProject, setExpandedProject] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [creatingNew, setCreatingNew] = useState(false);
    const [editingProject, setEditingProject] = useState<ProjectFA | undefined>(undefined);
    const [loadedForms, setLoadedForms] = useState<{ [projectId: string]: FormFA[] }>({});
    const [loadingForms, setLoadingForms] = useState<{ [projectId: string]: boolean }>({});
    const navigate = useNavigate();
    const {setSelectedProjectId, projects} = useProject();
    const [project, setProject] = useState<ProjectFA>(projects[0]);
    const [selectedForms, setSelectedForms] = useState<FormFA[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);

    const handleUpdateProject = (updatedProject: ProjectFA, forms?: FormFA[]) => {
        setProject(updatedProject);
        if (forms) {
            setLoadedForms(prev => ({
                ...prev,
                [updatedProject._id!]: forms
            }));
        }
        setEditingProject(undefined);
        setCreatingNew(false);
    };

    const handleDeleteProject = () => {
        setEditingProject(undefined);
        setCreatingNew(false);
    };

    const handleDeleteForm = (formId: string) => {
        setSelectedForms(prev => prev.filter(f => f._id !== formId));
        setLoadedForms(prev => ({
            ...prev,
            [editingProject!._id!]: prev[editingProject!._id!].filter(f => f._id !== formId)
        }));
    };

    const toggleProject = async (projectId: string) => {
        const isExpanding = expandedProject !== projectId;
        setExpandedProject(prev => (prev === projectId ? null : projectId));

        if (isExpanding && !loadedForms[projectId]) {
            setLoadingForms(prev => ({...prev, [projectId]: true}));

            try {
                const forms = await getFormsByProjectId(projectId);
                setLoadedForms(prev => ({...prev, [projectId]: forms}));
            } catch (error) {
                console.error(`Error loading forms for project ${projectId}:`, error);
                setLoadedForms(prev => ({...prev, [projectId]: []}));
            } finally {
                setLoadingForms(prev => ({...prev, [projectId]: false}));
            }
        }
    };

    useEffect(() => {
        if (projects.length > 0 || projects.length === 0) {
            setLoadingProjects(false);
        }
    }, [projects]);

    const filteredProjects = projects.filter(project => {
        const projectMatches = project.name.toLowerCase().includes(searchTerm.toLowerCase());

        const forms = loadedForms[project._id!] || [];
        const formMatches = forms.some(form =>
            form.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return projectMatches || formMatches;
    });

    useEffect(() => {
        document.title = 'Projects - FormsAWare';
    }, []);

    useEffect(() => {
        if (projectId && projects.length > 0) {
            const projectToEdit = projects.find(p => p._id === projectId);
            if (projectToEdit) {
                setEditingProject(projectToEdit);
                setSelectedForms(loadedForms[projectToEdit._id!] || []);
            }
        }
    }, [projectId, projects, loadedForms]);

    return (
        <div className="w-full h-full flex flex-col">
            {(creatingNew || editingProject) ? (
                <div className="w-full h-full flex flex-col">
                    <div className="flex gap-3 items-center mb-6 shrink-0">
                        <button
                            onClick={() => {
                                setCreatingNew(false);
                                setEditingProject(undefined);
                                setSelectedForms([])
                                navigate(`/projects`);
                            }}
                            className="size-9 flex items-center justify-center rounded-full text-ink-muted hover:text-primary hover:bg-surface-hover transition-colors"
                        >
                            <ArrowLeft size={18}/>
                        </button>
                        <h1 className="text-2xl font-display font-bold text-ink">
                            {editingProject ? 'Edit ' + editingProject.name : 'Create New Project'}
                        </h1>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                        <UpsertProject
                            project={editingProject}
                            forms={selectedForms}
                            onUpdate={handleUpdateProject}
                            onDeleteProject={handleDeleteProject}
                            onDeleteForm={handleDeleteForm}
                        />
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-6 shrink-0">
                        <div>
                            <h1 className="text-2xl font-display font-bold text-ink">Your Projects</h1>
                            <p className="text-sm text-ink-muted mt-1">Organize your forms into projects.</p>
                        </div>
                        <Button icon={Plus} onClick={() => setCreatingNew(true)}>
                            New Project
                        </Button>
                    </div>

                    <div className="mb-5 shrink-0">
                        <SearchInput
                            value={searchTerm}
                            onChange={setSearchTerm}
                            placeholder="Search projects or forms..."
                        />
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
                        {loadingProjects ? (
                            <Loader/>
                        ) : filteredProjects.length > 0 ? (
                            filteredProjects.map((project, i) => {
                                const isOpen = expandedProject === project._id;
                                const forms = loadedForms[project._id!];
                                return (
                                    <Card
                                        key={project._id}
                                        padding="none"
                                        className="animate-fade-in-up"
                                        style={staggerDelay(i)}
                                    >
                                        <div
                                            className="flex justify-between items-center cursor-pointer p-4"
                                            onClick={() => toggleProject(project._id!)}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="size-9 shrink-0 rounded-xl bg-primary-tint text-primary flex items-center justify-center">
                                                    <FolderKanban size={16}/>
                                                </div>
                                                <h2 className="text-sm font-semibold text-ink truncate">{project.name}</h2>
                                            </div>
                                            <ChevronDown
                                                size={18}
                                                className={`text-ink-faint shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                                            />
                                        </div>

                                        {isOpen && (
                                            <div className="px-4 pb-4 space-y-2 border-t border-border pt-4 animate-fade-in-down animate-duration-200">
                                                {loadingForms[project._id!] ? (
                                                    <Loader className="py-8"/>
                                                ) : forms?.length === 0 ? (
                                                    <p className="text-center text-sm text-ink-muted py-4">No forms created for this
                                                        project.</p>
                                                ) : (
                                                    forms?.map((form, i) => (
                                                        <div
                                                            key={form._id}
                                                            className="flex justify-between items-center bg-surface-sunken p-3 rounded-lg animate-fade-in-up"
                                                            style={staggerDelay(i, 30, 200)}
                                                        >
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <FileText size={14} className="text-ink-faint shrink-0"/>
                                                                <p className="text-sm text-ink truncate">{form.name}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedProjectId(project._id!);
                                                                    navigate(`/forms/${form._id}`);
                                                                }}
                                                                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline shrink-0 ml-3"
                                                            >
                                                                View Form
                                                                <ArrowUpRight size={12}/>
                                                            </button>
                                                        </div>
                                                    ))
                                                )}

                                                <div className="pt-2 flex justify-end">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        icon={Pencil}
                                                        onClick={() => {
                                                            setEditingProject(project)
                                                            setSelectedForms(loadedForms[project._id!] || []);
                                                            navigate(`/projects/${project._id}`);
                                                        }}
                                                    >
                                                        Edit project
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                );
                            })
                        ) : (
                            <EmptyState
                                icon={Search}
                                title="No projects or forms found"
                                description="Try a different search term, or create a new project."
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
